// Performance-Tab: Post-Statistiken aller Kanäle (aus om_poststats_reports) — Top-Posts,
// Format-Vergleich und Claude-Insight ("was lief gut / mehr davon").
import type { MarketingDeps } from "./marketing.js";

interface Post {
  network: string; type: string; url?: string; content?: string;
  reach: number; views: number; interactions: number; engagement: number; likes: number; comments: number;
}
interface FormatAgg { n: number; reach: number; interactions: number; views: number }
interface PerfSummary {
  empty?: boolean; generated_at?: string; report_at?: string; days?: number; total_posts?: number;
  by_format?: Record<string, FormatAgg>;
  top_overall?: Post[];
  insight?: { summary: string; recommendations: string[] } | null;
}

const NET_LABEL: Record<string, string> = { instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok", youtube: "YouTube", pinterest: "Pinterest" };
const n = (v: number): string => (v ?? 0).toLocaleString("de-DE");
const short = (s: string | undefined, len = 48): string => ((s ?? "").replace(/\s+/g, " ").trim().slice(0, len) || "—");

export function initPerformanceTab(m: HTMLElement, deps: MarketingDeps): void {
  const host = m.querySelector("[data-perf]") as HTMLElement | null;
  const msg = m.querySelector('[data-msg="performance"]') as HTMLElement | null;
  if (!host) return;

  const load = async (): Promise<void> => {
    host.innerHTML = '<p class="dim">Lade …</p>';
    const r = await deps.api<PerfSummary>("/api/marketing/performance");
    const d = r.data;
    if (!d || d.empty) {
      host.innerHTML = '<p class="dim">Noch keine Performance-Daten. Der Motor läuft wöchentlich (Mo) automatisch — oder jetzt per „↻ Statistiken ziehen“.</p>';
      return;
    }
    host.textContent = "";

    // Insight — als aufklappbarer Block (startet zugeklappt), damit der lange
    // Text die Seite nicht dauerhaft zumüllt.
    if (d.insight) {
      const det = document.createElement("details");
      det.className = "perf-insight";

      const sum = document.createElement("summary");
      const title = document.createElement("span");
      title.className = "perf-insight__title";
      title.textContent = "💡 Insights";
      sum.appendChild(title);
      const when = d.report_at || d.generated_at;
      if (when) {
        const today = new Date().toDateString() === new Date(when).toDateString();
        const ds = document.createElement("span");
        ds.className = "perf-insight__date";
        ds.textContent = today
          ? "von heute, " + new Date(when).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })
          : "vom " + new Date(when).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
        sum.appendChild(ds);
      }
      const rc = d.insight.recommendations?.length ?? 0;
      const hint = document.createElement("span");
      hint.className = "perf-insight__hint";
      hint.textContent = rc ? `${rc} Empfehlungen` : "ansehen";
      sum.appendChild(hint);
      det.appendChild(sum);

      const body = document.createElement("div");
      body.className = "perf-insight__body";
      const p = document.createElement("p"); p.textContent = d.insight.summary;
      body.appendChild(p);
      if (d.insight.recommendations?.length) {
        const ul = document.createElement("ul");
        for (const rec of d.insight.recommendations) { const li = document.createElement("li"); li.textContent = rec; ul.appendChild(li); }
        body.appendChild(ul);
      }
      det.appendChild(body);
      host.appendChild(det);
    }

    const meta = document.createElement("p");
    meta.className = "dim";
    meta.textContent = `${d.total_posts ?? 0} Posts · letzte ${d.days ?? 90} Tage${d.report_at ? " · Stand " + new Date(d.report_at).toLocaleString("de-DE") : ""}`;
    host.appendChild(meta);

    // Format-Vergleich
    const fmts = Object.entries(d.by_format ?? {}).sort((a, b) => (b[1].reach / b[1].n) - (a[1].reach / a[1].n));
    if (fmts.length) {
      const h = document.createElement("h3"); h.textContent = "Formate im Vergleich (ø pro Post)"; host.appendChild(h);
      const t = document.createElement("table"); t.className = "om-table";
      const hr = t.createTHead().insertRow();
      for (const l of ["Format", "Posts", "ø Reach", "ø Interaktionen", "ø Views"]) { const th = document.createElement("th"); th.textContent = l; hr.appendChild(th); }
      const tb = t.createTBody();
      for (const [f, v] of fmts) {
        const tr = tb.insertRow();
        tr.insertCell().textContent = f;
        tr.insertCell().textContent = n(v.n);
        tr.insertCell().textContent = n(Math.round(v.reach / v.n));
        tr.insertCell().textContent = n(Math.round(v.interactions / v.n));
        tr.insertCell().textContent = n(Math.round(v.views / v.n));
      }
      host.appendChild(t);
    }

    // Top-Posts
    const top = d.top_overall ?? [];
    if (top.length) {
      const h = document.createElement("h3"); h.textContent = "Top-Posts (nach Reach/Views)"; host.appendChild(h);
      const t = document.createElement("table"); t.className = "om-table";
      const hr = t.createTHead().insertRow();
      for (const l of ["Kanal", "Format", "Reach", "Views", "Interakt.", "Inhalt"]) { const th = document.createElement("th"); th.textContent = l; hr.appendChild(th); }
      const tb = t.createTBody();
      for (const p of top) {
        const tr = tb.insertRow();
        tr.insertCell().textContent = NET_LABEL[p.network] ?? p.network;
        tr.insertCell().textContent = p.type || "—";
        tr.insertCell().textContent = n(p.reach);
        tr.insertCell().textContent = n(p.views);
        tr.insertCell().textContent = n(p.interactions);
        const c = tr.insertCell();
        if (p.url) { const a = document.createElement("a"); a.href = p.url; a.target = "_blank"; a.rel = "noopener"; a.textContent = short(p.content); c.appendChild(a); }
        else c.textContent = short(p.content);
      }
      host.appendChild(t);
    }
  };

  m.querySelector('[data-action="run-poststats"]')?.addEventListener("click", () => {
    void (async () => {
      if (msg) msg.textContent = "Starte Statistik-Pull …";
      const r = await deps.api<{ stamp?: string }>("/api/marketing/motors/social_poststats/run", "POST");
      if (msg) msg.textContent = r.status === 200 ? "Läuft — Tab in ~1 Min neu öffnen." : r.status === 409 ? "Läuft bereits." : `Fehler (${r.status})`;
    })();
  });

  void load();
}
